import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, ChevronRight } from 'lucide-react';
import { presentationGuildRankings, presentationRankings } from '../data/presentationData';
type Language = 'es' | 'en' | 'pt';
const translations = {
  es: {
    title: 'Mejores',
    titleHighlight: 'Guerreros',
    viewFullRankings: 'Ver Rankings Completos',
    rank: 'Rango',
    character: 'Personaje',
    class: 'Clase',
    level: 'Nivel',
    resets: 'Resets',
    topGuilds: 'Mejores Clanes',
    guild: 'Clan',
    master: 'Líder',
    members: 'Miembros',
    score: 'Puntos'
  },
  en: {
    title: 'Top',
    titleHighlight: 'Warriors',
    viewFullRankings: 'View Full Rankings',
    rank: 'Rank',
    character: 'Character',
    class: 'Class',
    level: 'Level',
    resets: 'Resets',
    topGuilds: 'Top Guilds',
    guild: 'Guild',
    master: 'Master',
    members: 'Members',
    score: 'Score'
  },
  pt: {
    title: 'Melhores',
    titleHighlight: 'Guerreiros',
    viewFullRankings: 'Ver Rankings Completos',
    rank: 'Rank',
    character: 'Personagem',
    class: 'Classe',
    level: 'Nível',
    resets: 'Resets',
    topGuilds: 'Melhores Clãs',
    guild: 'Clã',
    master: 'Líder',
    members: 'Membros',
    score: 'Pontos'
  }
};
const topPlayers = presentationRankings.map(player => ({
  rank: player.rank,
  name: player.characterName,
  class: player.characterClass,
  level: player.level,
  resets: player.resets
}));
const topGuilds = presentationGuildRankings.map(guild => ({
  rank: guild.rank,
  name: guild.guildName,
  master: guild.guildMaster,
  members: guild.members,
  score: guild.score
}));
export function Rankings() {
  const [language, setLanguage] = useState<Language>('es');
  const t = translations[language];
  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang) setLanguage(savedLang);
    const handleLanguageChange = (e: CustomEvent<Language>) => {
      setLanguage(e.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChange', handleLanguageChange as EventListener);
  }, []);
  return <section id="rankings" className="py-16 md:py-24 relative z-10 bg-darkerBlue overflow-hidden" data-id="element-424">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-425">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-426">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 flex items-center justify-center gap-4" data-id="element-427">
            <Trophy className="w-10 h-10 text-gold" data-id="element-428" />
            {t.title}{' '}
            <span className="text-gradient-gold" data-id="element-429">{t.titleHighlight}</span>
          </motion.h2>
          <motion.div initial={{
          opacity: 0,
          scale: 0
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="w-24 h-1 bg-gold mx-auto rounded-full" data-id="element-430" />
        </div>

        {/* Two Column Layout - Top Players */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-16" data-id="element-431">
          {/* Left: Animated Character */}
          <motion.div initial={{
          opacity: 0,
          x: -50,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          x: 0,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          ease: 'easeOut'
        }} className="flex items-center justify-center" data-id="element-432">
            <div className="relative w-full max-w-lg lg:max-w-xl" data-id="element-433">
              <motion.img src="https://cdn.magicpatterns.com/uploads/vmSVXSm7qDFoDrzShhddTr/ChatGPT_Image_Mar_25,_2026,_06_24_09_PM.png" alt="MU Vanguard Gladiator" className="w-full h-auto object-contain drop-shadow-[0_0_30px_rgba(212,175,55,0.4)] md:drop-shadow-[0_0_60px_rgba(212,175,55,0.5)]" animate={{
              y: [0, -10, 0] // Reduced movement on mobile for smoother effect
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-434" />
              {/* Glow effect behind character */}
              <div className="absolute inset-0 bg-gradient-to-br from-gold/30 via-glowBlue/30 to-purple-500/20 rounded-full blur-3xl -z-10" data-id="element-435" />
            </div>
          </motion.div>

          {/* Right: Top Players Table */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-436">
            <div className="mb-6 flex justify-end" data-id="element-437">
              <motion.a initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} href="#full-rankings" className="flex items-center gap-2 text-gold hover:text-gold-light transition-colors font-cinzel font-bold group" data-id="element-438">
                {t.viewFullRankings}
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-439" />
              </motion.a>
            </div>

            <div className="glass-panel rounded-2xl overflow-hidden border border-white/10" data-id="element-440">
              <div className="overflow-x-auto" data-id="element-441">
                <table className="w-full text-left border-collapse" data-testid="player-rankings-table" data-field="rankings.players" data-id="element-442">
                  <thead data-id="element-443">
                    <tr className="bg-darkBlue/80 border-b border-white/10 text-gray-400 font-cinzel text-sm uppercase tracking-wider" data-id="element-444">
                      <th className="p-5 font-semibold" data-id="element-445">{t.rank}</th>
                      <th className="p-5 font-semibold" data-id="element-446">{t.character}</th>
                      <th className="p-5 font-semibold" data-id="element-447">{t.class}</th>
                      <th className="p-5 font-semibold text-right" data-id="element-448">
                        {t.level}
                      </th>
                      <th className="p-5 font-semibold text-right" data-id="element-449">
                        {t.resets}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5" data-id="element-450">
                    {topPlayers.map((player, index) => <tr key={player.rank} data-testid="player-ranking-row" data-field={`rankings.players.${player.rank}`} className="hover:bg-white/5 transition-colors group" data-id="element-451">
                        <td className="p-5" data-id="element-452">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold font-cinzel
                        ${index === 0 ? 'bg-gold text-darkerBlue shadow-[0_0_15px_rgba(212,175,55,0.5)]' : index === 1 ? 'bg-gray-300 text-darkerBlue' : index === 2 ? 'bg-[#CD7F32] text-darkerBlue' : 'bg-darkBlue text-gray-400 border border-white/10'}`} data-id="element-453">
                            {player.rank}
                          </div>
                        </td>
                        <td className="p-5 font-bold text-white group-hover:text-gold transition-colors" data-id="element-454">
                          {player.name}
                        </td>
                        <td className="p-5 text-gray-300" data-id="element-455">{player.class}</td>
                        <td className="p-5 text-right font-mono text-glowBlue" data-id="element-456">
                          {player.level}
                        </td>
                        <td className="p-5 text-right font-mono text-gold-light" data-id="element-457">
                          {player.resets}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Two Column Layout - Top Guilds */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center" data-id="element-458">
          {/* Left: Top Guilds Table */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-459">
            <div className="mb-6" data-id="element-460">
              <h3 className="text-2xl md:text-3xl font-bold text-white font-cinzel" data-id="element-461">
                {t.topGuilds}
              </h3>
              <div className="w-16 h-1 bg-gold rounded-full mt-2" data-id="element-462" />
            </div>

            <div className="glass-panel rounded-2xl overflow-hidden border border-white/10" data-id="element-463">
              <div className="overflow-x-auto" data-id="element-464">
                <table className="w-full text-left border-collapse" data-testid="guild-rankings-table" data-field="rankings.guilds" data-id="element-465">
                  <thead data-id="element-466">
                    <tr className="bg-darkBlue/80 border-b border-white/10 text-gray-400 font-cinzel text-sm uppercase tracking-wider" data-id="element-467">
                      <th className="p-5 font-semibold" data-id="element-468">{t.rank}</th>
                      <th className="p-5 font-semibold" data-id="element-469">{t.guild}</th>
                      <th className="p-5 font-semibold" data-id="element-470">{t.master}</th>
                      <th className="p-5 font-semibold text-right" data-id="element-471">
                        {t.members}
                      </th>
                      <th className="p-5 font-semibold text-right" data-id="element-472">
                        {t.score}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5" data-id="element-473">
                    {topGuilds.map((guild, index) => <tr key={guild.rank} data-testid="guild-ranking-row" data-field={`rankings.guilds.${guild.rank}`} className="hover:bg-white/5 transition-colors group" data-id="element-474">
                        <td className="p-5" data-id="element-475">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold font-cinzel
                        ${index === 0 ? 'bg-gold text-darkerBlue shadow-[0_0_15px_rgba(212,175,55,0.5)]' : index === 1 ? 'bg-gray-300 text-darkerBlue' : index === 2 ? 'bg-[#CD7F32] text-darkerBlue' : 'bg-darkBlue text-gray-400 border border-white/10'}`} data-id="element-476">
                            {guild.rank}
                          </div>
                        </td>
                        <td className="p-5 font-bold text-white group-hover:text-gold transition-colors" data-id="element-477">
                          {guild.name}
                        </td>
                        <td className="p-5 text-gray-300" data-id="element-478">{guild.master}</td>
                        <td className="p-5 text-right font-mono text-glowBlue" data-id="element-479">
                          {guild.members}
                        </td>
                        <td className="p-5 text-right font-mono text-gold-light" data-id="element-480">
                          {guild.score.toLocaleString()}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>

          {/* Right: Animated Character */}
          <motion.div initial={{
          opacity: 0,
          x: 50,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          x: 0,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          ease: 'easeOut'
        }} className="flex items-center justify-center" data-id="element-481">
            <div className="relative w-full max-w-lg lg:max-w-xl" data-id="element-482">
              <img src="https://cdn.magicpatterns.com/uploads/vvzqDPxJ1BFaR3XDime2bz/ChatGPT_Image_Mar_25,_2026,_08_05_16_PM.png" alt="MU Vanguard Dark Knight" className="w-full h-auto object-contain max-w-md mx-auto drop-shadow-[0_0_30px_rgba(212,175,55,0.4)] md:drop-shadow-[0_0_60px_rgba(212,175,55,0.5)]" data-id="element-483" />
              {/* Glow effect behind character */}
              <div className="absolute inset-0 bg-gradient-to-br from-gold/30 via-glowBlue/30 to-purple-500/20 rounded-full blur-3xl -z-10" data-id="element-484" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}